import { hasAnyPermission, isSuperAdmin } from '@/shared/helpers/user'
import type { UserProfile } from '@/shared/types/user.type'

export interface BigSaleModuleMenu {
  title: string
  icon: string
  path: string
  permissions: string[]
  hidden?: boolean
  routeMode?: 'module' | 'standalone'
}

export interface BigSaleModule {
  id: string
  code: string
  title: string
  icon: string
  path: string
  order: number
  permissions: string[]
  menus: BigSaleModuleMenu[]
}

const moduleDefinitions: BigSaleModule[] = [
  //product
  {
    id: 'product',
    code: 'PRODUCT',
    title: 'Hàng hóa',
    icon: 'mdi-package-variant-closed',
    path: '/products',
    order: 4,
    permissions: ['product.view', 'sale.online.view'],
    menus: [
      {
        title: 'Sản phẩm',
        icon: 'mdi-package-variant',
        path: '/products',
        permissions: ['product.view'],
      },
      {
        title: 'Thương hiệu',
        icon: 'mdi-tag-outline',
        path: '/products/brands',
        permissions: ['product.brand.view'],
      },
      {
        title: 'Đơn vị tính',
        icon: 'mdi-ruler-square',
        path: '/settings/units',
        permissions: ['product.unit.view'],
      },
      {
        title: 'Nhóm sản phẩm',
        icon: 'mdi-folder-multiple-outline',
        path: '/settings/product-groups',
        permissions: ['product.category.view'],
      },
      {
        title: 'Vị trí',
        icon: 'mdi-map-marker-distance',
        path: '/products/locations',
        permissions: ['product.location.view'],
      },
      {
        title: 'Bán Online',
        icon: 'mdi-web',
        path: '/products/sale-online',
        permissions: ['sale.online.view'],
        hidden: true,
      },
    ],
  },

  //price
  {
    id: 'price',
    code: 'PRICE',
    title: 'Thiết lập giá',
    icon: 'mdi-cash-multiple',
    path: '/prices',
    order: 5,
    permissions: ['price.view'],
    menus: [
      {
        title: 'Bảng giá sản phẩm',
        icon: 'mdi-tag-outline',
        path: '/prices/products',
        permissions: ['price_list.view'],
      },
      // {
      //   title: 'Chương trình khuyến mãi',
      //   icon: 'mdi-sale-outline',
      //   path: '/promotions',
      //   permissions: ['promotion.view'],
      // },
      {
        title: 'Mã giảm giá',
        icon: 'mdi-ticket-percent-outline',
        path: '/promotions/coupons',
        permissions: ['promotion.coupons.view'],
      },
    ],
  },

  //pos
  {
    id: 'pos',
    code: 'POS',
    title: 'Bán hàng',
    icon: 'mdi-cash-register',
    path: '/sales',
    order: 2,
    permissions: ['pos.view'],
    menus: [
      {
        title: 'Bán theo bàn',
        icon: 'mdi-table-chair',
        path: '/pos',
        permissions: ['pos.view'],
        routeMode: 'standalone',
      },
      {
        title: 'Bán thường',
        icon: 'mdi-cart-outline',
        path: '/sales',
        permissions: ['sales_invoice.view'],
      },
      {
        title: 'Bếp bar',
        icon: 'mdi-silverware-fork-knife',
        path: '/pos/kitchen',
        permissions: ['pos.kitchen.view'],
        routeMode: 'standalone',
      },
      {
        title: 'Đặt hàng',
        icon: 'mdi-cart-arrow-down',
        path: '/sales-orders',
        permissions: ['sales_order.view'],
      },
    ],
  },

  //area-table
  {
    id: 'table',
    code: 'TABLE',
    title: 'Quản lý bàn',
    icon: 'mdi-table-chair',
    path: '/tables',
    order: 3,
    permissions: ['sales_table.view', 'area_pricing.view'],
    menus: [
      {
        title: 'Khu vực',
        icon: 'mdi-map-marker-radius-outline',
        path: '/tables/areas',
        permissions: ['sales_area.view'],
      },
      {
        title: 'Danh sách bàn',
        icon: 'mdi-table-furniture',
        path: '/tables',
        permissions: ['sales_table.view'],
      },
      {
        title: 'Giá khu vực',
        icon: 'mdi-table-clock',
        path: '/prices/areas',
        permissions: ['area_pricing.view'],
      },
    ],
  },

  //warehouse
  {
    id: 'inventory',
    code: 'INVENTORY',
    title: 'Kho hàng',
    icon: 'mdi-warehouse',
    path: '/inventory',
    order: 6,
    permissions: ['inventory.view'],
    menus: [
      {
        title: 'Tồn kho',
        icon: 'mdi-package-variant-closed',
        path: '/inventory/stocks',
        permissions: ['inventory.stock.view'],
        hidden: true,
      },
      {
        title: 'Tồn đầu kỳ',
        icon: 'mdi-database-plus-outline',
        path: '/inventory/opening-stocks',
        permissions: ['inventory.opening_stock.view'],
        hidden: true,
      },
      {
        title: 'Nhập hàng',
        icon: 'mdi-receipt-text-plus-outline',
        path: '/inventory/purchase-receipts',
        permissions: ['inventory.purchase_receipt.view'],
      },
      {
        title: 'Trả hàng nhập',
        icon: 'mdi-keyboard-return',
        path: '/inventory/purchase-returns',
        permissions: ['inventory.purchase_return.view'],
      },
      {
        title: 'Chuyển hàng',
        icon: 'mdi-swap-horizontal-bold',
        path: '/inventory/transfer-orders',
        permissions: ['inventory.transfer_order.view'],
      },
      {
        title: 'Nhà cung cấp',
        icon: 'mdi-truck-outline',
        path: '/inventory/suppliers',
        permissions: ['supplier.view'],
      },
      {
        title: 'Phiếu sản xuất',
        icon: 'mdi-factory',
        path: '/inventory/production-orders',
        permissions: ['manufacturing.production_order.view'],
      },
      {
        title: 'Kiểm kho',
        icon: 'mdi-clipboard-check-outline',
        path: '/inventory/checks',
        permissions: ['inventory.check.view'],
      },
      {
        title: 'Lịch sử kho',
        icon: 'mdi-history',
        path: '/inventory/histories',
        permissions: ['inventory.movement.view'],
        hidden: true,
      },
    ],
  },

  //transaction
  {
    id: 'order',
    code: 'ORDER',
    title: 'Giao dịch',
    icon: 'mdi-receipt-text-outline',
    path: '/orders',
    order: 7,
    permissions: [
      'sales_invoice.view',
      'bill.view',
      'cash_book.view',
      'cash_transaction.view',
      'sale_return.view',
    ],
    menus: [
      {
        title: 'Hóa đơn',
        icon: 'mdi-receipt-outline',
        path: '/orders',
        permissions: ['sales_invoice.view', 'bill.view'],
      },
      {
        title: 'Đặt hàng',
        icon: 'mdi-cart-outline',
        path: '/sales-orders',
        permissions: ['sales_order.view'],
      },
      {
        title: 'Trả hàng / Đổi hàng',
        icon: 'mdi-backup-restore',
        path: '/orders/returns',
        permissions: ['sale_return.view'],
      },
      {
        title: 'Sổ quỹ',
        icon: 'mdi-notebook-outline',
        path: '/orders/cash-book',
        permissions: ['cash_book.view'],
      },
      {
        title: 'Thu/chi',
        icon: 'mdi-cash-multiple',
        path: '/orders/cash-transactions',
        permissions: ['cash_transaction.view'],
      },
      {
        title: 'Loại thu chi',
        icon: 'mdi-format-list-bulleted-type',
        path: '/orders/cash-book-types',
        permissions: ['cash_book_type.view'],
      },
    ],
  },

  //customer
  {
    id: 'customer',
    code: 'CUSTOMER',
    title: 'Khách hàng',
    icon: 'mdi-account-group-outline',
    path: '/customers',
    order: 8,
    permissions: ['customer.view'],
    menus: [
      {
        title: 'Danh sách khách hàng',
        icon: 'mdi-account-multiple-outline',
        path: '/customers',
        permissions: ['customer.view'],
      },
      {
        title: 'Nhóm khách hàng',
        icon: 'mdi-account-supervisor-outline',
        path: '/customers/groups',
        permissions: ['customer_group.view'],
      },
      {
        title: 'Công nợ khách hàng',
        icon: 'mdi-cash-clock',
        path: '/customers/debts',
        permissions: ['customer_debt.view'],
      },
      {
        title: 'Hạng thành viên',
        icon: 'mdi-star-outline',
        path: '/customers/tiers',
        permissions: ['membership_tier.view'],
      },
      {
        title: 'Tích điểm thành viên',
        icon: 'mdi-star-circle-outline',
        path: '/customers/loyalty-points',
        permissions: ['loyalty.point_policy.view'],
      },
    ],
  },

  //human resource
  {
    id: 'staff',
    code: 'STAFF',
    title: 'Nhân viên',
    icon: 'mdi-account-tie-outline',
    path: '/staffs',
    order: 10,
    permissions: [
      'staff.view',
      'staff_shift.view',
      'staff_attendance.view_self',
      'staff_attendance.view_all',
      'staff_attendance.punch',
      'staff_attendance.online_punch',
      'staff_attendance.confirm_for_staff',
      'staff_leave.view_self',
      'staff_leave.manage',
      'staff_overtime.view_self',
      'staff_overtime.manage',
      'staff_allowance.view',
      'staff_allowance.manage',
      'staff_salary_advance.view',
      'staff_salary_advance.manage',
      'staff_salary_advance.request',
      'staff_insurance.manage',
      'staff_payroll.view',
      'staff_payroll.view_self',
      'staff_offboarding.view',
      'staff_offboarding.view_self',
    ],
    menus: [
      {
        title: 'Danh sách nhân viên',
        icon: 'mdi-account-box-outline',
        path: '/staffs',
        permissions: ['staff.view'],
      },
      {
        title: 'Hợp đồng lao động',
        icon: 'mdi-file-sign',
        path: '/staffs/contracts',
        permissions: ['staff_contract.view', 'staff_contract.view_self'],
      },
      {
        title: 'Loại hợp đồng',
        icon: 'mdi-format-list-bulleted-type',
        path: '/staffs/contracts/types',
        permissions: [
          'staff_contract.type_view',
          'staff_contract.type_create',
          'staff_contract.type_update',
          'staff_contract.type_delete',
        ],
      },
      {
        title: 'Nghỉ việc',
        icon: 'mdi-account-arrow-right-outline',
        path: '/staffs/offboardings',
        permissions: ['staff_offboarding.view', 'staff_offboarding.view_self'],
      },
      {
        title: 'Bộ phận',
        icon: 'mdi-domain',
        path: '/staffs/departments',
        permissions: ['staff_department.view'],
      },
      {
        title: 'Ca làm việc',
        icon: 'mdi-calendar-clock-outline',
        path: '/staffs/shifts',
        permissions: ['staff_shift.view'],
      },
      {
        title: 'Chấm công',
        icon: 'mdi-fingerprint',
        path: '/staffs/attendances',
        permissions: ['staff_attendance.view_self', 'staff_attendance.view_all', 'staff_attendance.confirm_for_staff'],
      },
      {
        title: 'Chấm công trực tuyến',
        icon: 'mdi-map-marker-check-outline',
        path: '/staffs/attendances/online',
        permissions: ['staff_attendance.online_punch'],
      },
      {
        title: 'Lịch sử điểm danh',
        icon: 'mdi-calendar-account-outline',
        path: '/staffs/attendances/history',
        permissions: ['staff_attendance.view_self', 'staff_attendance.view_all', 'staff_attendance.confirm_for_staff'],
      },
      {
        title: 'Thống kê công tháng',
        icon: 'mdi-calendar-month-outline',
        path: '/staffs/attendances/monthly-overview',
        permissions: ['staff_attendance.view_self', 'staff_attendance.view_all'],
      },
      {
        title: 'Thống kê tổng thời gian',
        icon: 'mdi-table-clock',
        path: '/staffs/attendances/total-time',
        permissions: ['staff_attendance.view_self', 'staff_attendance.view_all'],
      },
      {
        title: 'Nghỉ phép',
        icon: 'mdi-calendar-remove-outline',
        path: '/staffs/leave',
        permissions: ['staff_leave.view_self', 'staff_leave.manage'],
      },
      {
        title: 'Phiếu tăng ca',
        icon: 'mdi-clock-plus-outline',
        path: '/staffs/overtime-requests',
        permissions: ['staff_overtime.view_self', 'staff_overtime.manage'],
      },
      {
        title: 'Phụ cấp',
        icon: 'mdi-cash-plus',
        path: '/staffs/allowances',
        permissions: ['staff_allowance.view', 'staff_allowance.manage'],
      },
      {
        title: 'Ứng lương',
        icon: 'mdi-cash-fast',
        path: '/staffs/salary-advances',
        permissions: [
          'staff_salary_advance.view',
          'staff_salary_advance.manage',
          'staff_salary_advance.request',
        ],
      },
      {
        title: 'Bảo hiểm',
        icon: 'mdi-shield-account-outline',
        path: '/staffs/insurance',
        permissions: ['staff_insurance.view', 'staff_insurance.manage'],
      },
      {
        title: 'Bảng lương',
        icon: 'mdi-file-document-multiple-outline',
        path: '/staffs/payroll',
        permissions: ['staff_payroll.view', 'staff_payroll.view_self'],
      },
      {
        title: 'Phiếu lương',
        icon: 'mdi-receipt-text-outline',
        path: '/staffs/payroll/payslips',
        permissions: ['staff_payroll.view', 'staff_payroll.view_self'],
      },
      {
        title: 'Phân quyền',
        icon: 'mdi-shield-account-outline',
        path: '/staffs/roles',
        permissions: ['role.view'],
      },
    ],
  },

  //report
  {
    id: 'report',
    code: 'REPORT',
    title: 'Báo cáo',
    icon: 'mdi-chart-bar',
    path: '/reports',
    order: 11,
    permissions: [
      'report.view',
      'shift_revenue_report.view_self',
      'shift_revenue_report.view_all',
      'sale.online.view',
    ],
    menus: [
      {
        title: 'Tổng quan hệ thống',
        icon: 'mdi-chart-box-outline',
        path: '/dashboard',
        permissions: ['dashboard.view'],
      },
      {
        title: 'Báo cáo tài chính',
        icon: 'mdi-finance',
        path: '/dashboard/reports-finance',
        permissions: ['dashboard.view'],
      },
      {
        title: 'Báo cáo doanh thu',
        icon: 'mdi-chart-line',
        path: '/dashboard/reports-revenue',
        permissions: ['dashboard.view'],
      },
      {
        title: 'Doanh thu theo ca',
        icon: 'mdi-clock-time-eight-outline',
        path: '/reports/shift-revenue',
        permissions: ['shift_revenue_report.view_self', 'shift_revenue_report.view_all'],
      },
      {
        title: 'Báo cáo chốt ca',
        icon: 'mdi-clipboard-check-outline',
        path: '/reports/shift-closing',
        permissions: ['shift_closing_report.view_self', 'shift_closing_report.view_all'],
      },
      {
        title: 'Báo cáo kết thúc ngày',
        icon: 'mdi-calendar-check-outline',
        path: '/reports/day-closing',
        permissions: ['day_closing_report.view'],
      },
      {
        title: 'Báo cáo bán hàng',
        icon: 'mdi-cart-arrow-up',
        path: '/reports/sales',
        permissions: ['report.sale.view'],
      },
      {
        title: 'Báo cáo sản phẩm',
        icon: 'mdi-package-variant',
        path: '/reports/products',
        permissions: ['report.product.view'],
      },
      {
        title: 'Báo cáo kho',
        icon: 'mdi-warehouse',
        path: '/reports/inventory',
        permissions: ['report.inventory.view'],
      },
      {
        title: 'Báo cáo nhân viên',
        icon: 'mdi-account-tie-outline',
        path: '/reports/staffs',
        permissions: ['report.staff.view'],
      },
      {
        title: 'Thống kê online',
        icon: 'mdi-web',
        path: '/reports/online-stats',
        permissions: ['sale.online.view'],
        hidden: true,
      },
    ],
  },

  //setting
  {
    id: 'setting',
    code: 'SETTING',
    title: 'Cài đặt',
    icon: 'mdi-cog-outline',
    path: '/settings',
    order: 12,
    permissions: [
      'setting.view',
      'staff_payroll.policy_manage',
      'sale.online.update',
      'delivery_partner.view',
      'product.unit.view',
      'product.category.view',
      'billiard_light.view',
    ],
    menus: [
      {
        title: 'Quản lý chi nhánh',
        icon: 'mdi-store-outline',
        path: '/settings/branches',
        permissions: ['branch.view'],
      },
      {
        title: 'Thiết bị đèn bàn',
        icon: 'mdi-lightbulb-on-outline',
        path: '/settings/billiard-lights',
        permissions: ['billiard_light.view'],
      },
      {
        title: 'Máy in',
        icon: 'mdi-printer-outline',
        path: '/settings/printers',
        permissions: ['setting.printers.view'],
      },
      {
        title: 'Mẫu in hóa đơn',
        icon: 'mdi-receipt-text-edit-outline',
        path: '/settings/print-templates',
        permissions: ['setting.print_templates.view'],
      },
      {
        title: 'Phương thức thanh toán',
        icon: 'mdi-cash-multiple',
        path: '/settings/payment-methods',
        permissions: ['setting.payment_methods.view'],
      },
      {
        title: 'Đối tác vận chuyển',
        icon: 'mdi-truck-delivery-outline',
        path: '/settings/delivery-partners',
        permissions: ['delivery_partner.view'],
      },
      {
        title: 'Lịch sử hệ thống',
        icon: 'mdi-history',
        path: '/settings/activity-logs',
        permissions: ['activity_log.view'],
      },
      {
        title: 'Cấu hình hệ thống',
        icon: 'mdi-tune',
        path: '/settings/system',
        permissions: ['setting.system.view', 'staff_payroll.policy_manage'],
      },
      {
        title: 'Cấu hình web online',
        icon: 'mdi-palette-outline',
        path: '/settings/online-web',
        permissions: ['sale.online.update'],
        hidden: true,
      },
      {
        title: 'Nhà phát triển',
        icon: 'mdi-information-outline',
        path: '/settings/about',
        permissions: [],
      },
    ],
  },
]

const inventoryModule = moduleDefinitions.find((module) => module.id === 'inventory')
const priceModule = moduleDefinitions.find((module) => module.id === 'price')

export const bigSaleModules: BigSaleModule[] = moduleDefinitions
  .filter((module) => module.id !== 'inventory' && module.id !== 'price')
  .map((module) => {
    if (module.id !== 'product') {
      return module
    }

    const [productMenu, ...remainingProductMenus] = module.menus

    return {
      ...module,
      permissions: [
        ...new Set([
          ...module.permissions,
          ...(priceModule?.permissions ?? []),
          ...(inventoryModule?.permissions ?? []),
        ]),
      ],
      menus: [
        ...(productMenu ? [productMenu] : []),
        ...(priceModule?.menus ?? []),
        ...remainingProductMenus,
        ...(inventoryModule?.menus ?? []),
      ],
    }
  })

export function getAllowedModules(user: UserProfile | null) {
  if (!user) {
    return []
  }

  if (isSuperAdmin(user)) {
    return bigSaleModules
      .map((module) => ({
        ...module,
        menus: module.menus.filter((menu) => !menu.hidden),
      }))
      .filter((module) => module.menus.length > 0)
  }

  return bigSaleModules
    .map((module) => ({
      ...module,
      menus: module.menus.filter(
        (menu) => !menu.hidden && hasAnyPermission(user, menu.permissions),
      ),
    }))
    .filter((module) => module.menus.length > 0)
}

export function getModuleEntryPath(module: Pick<BigSaleModule, 'menus' | 'path'>) {
  return module.menus[0]?.path ?? module.path
}

export function getModuleTitleKey(module: Pick<BigSaleModule, 'id'>) {
  return `navigation.modules.${module.id}`
}

export function getModuleMenuTitleKey(
  module: Pick<BigSaleModule, 'id' | 'path'>,
  menu: BigSaleModuleMenu,
) {
  if (module.id === 'product' && menu.path === '/settings/units') {
    return 'navigation.menus.product.units'
  }

  if (module.id === 'product' && menu.path === '/settings/product-groups') {
    return 'navigation.menus.product.groups'
  }

  if (menu.path.startsWith('/inventory/')) {
    const inventoryChildPath = menu.path
      .replace('/inventory/', '')
      .replace(/-/g, '_')
      .replace(/\//g, '.')

    return `navigation.menus.inventory.${inventoryChildPath}`
  }

  if (menu.path === module.path) {
    return `navigation.menus.${module.id}.index`
  }

  const childPath = menu.path
    .replace(`${module.path}/`, '')
    .replace(/^\/+/, '')
    .replace(/-/g, '_')
    .replace(/\//g, '.')

  return `navigation.menus.${module.id}.${childPath}`
}
